import { useState, useMemo } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useBrandColors } from '@/hooks/useBrandColors';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TenantSwitcher } from '@/components/TenantSwitcher';
import { SuperadminBanner } from '@/components/SuperadminBanner';
import { TrialBanner } from '@/components/TrialBanner';
import { SubscriptionBanner } from '@/components/SubscriptionBanner';
import { UserAvatar } from '@/components/UserAvatar';
import { useOnboardingTour } from '@/components/OnboardingTour';
import { SupportTicketButton } from '@/components/SupportTicketButton';
import { OnboardingInterstitial } from '@/components/OnboardingInterstitial';
import { getNavigationConfig } from '@/lib/navigation';
import { authAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  ChevronDown,
  ChevronRight,
  LogOut,
  User,
  Moon,
  Sun,
  Settings,
  Menu,
  X,
  CreditCard,
  Building2,
  Shield,
  CheckCircle,
  Activity,
  Eye,
  Gift,
  Plug,
  Palette,
  DollarSign,
  Zap,
  FileCode,
  ExternalLink,
} from 'lucide-react';

// Logo URLs
const VISTRY_LOGO_URL = "/vistry-icon.png";
const EVA_LOGO_URL = "/eva-icon-square.png";

function NavGroup({ item, isOpen, onToggle, onNavigate }) {
  const location = useLocation();
  const isActive = item.children?.some(child => location.pathname.startsWith(child.path)) || location.pathname === item.path;
  const Icon = item.icon;

  if (!item.children) {
    return (
      <NavLink
        to={item.path}
        onClick={onNavigate}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
            isActive
              ? 'nav-active'
              : 'text-muted-foreground hover:bg-secondary'
          }`
        }
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <Icon className="w-4 h-4" />
        {item.label}
      </NavLink>
    );
  }

  return (
    <Collapsible open={isOpen || isActive} onOpenChange={onToggle}>
      <CollapsibleTrigger 
        className={`flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
          isActive 
            ? 'text-foreground' 
            : 'text-muted-foreground hover:bg-secondary'
        }`}
        data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <span className="flex items-center gap-3">
          <Icon className="w-4 h-4" />
          {item.label}
        </span>
        {isOpen || isActive ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </CollapsibleTrigger>
      <CollapsibleContent className="pl-4 mt-1 space-y-1">
        {item.children.map((child) => (
          <NavLink
            key={child.path}
            to={child.path}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-all duration-200 ${
                isActive
                  ? 'nav-active'
                  : 'text-muted-foreground hover:bg-secondary/80 dark:hover:bg-secondary/50'
              }`
            }
            data-testid={`nav-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <child.icon className="w-4 h-4" />
            {child.label}
          </NavLink>
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

const ROLE_BADGE_CLASS = 'bg-primary/10 text-primary';

const ROLE_CONFIG = {
  admin: { label: 'Admin', icon: Shield },
  approver: { label: 'Approver', icon: CheckCircle },
  analyst: { label: 'Analyst', icon: Activity },
  viewer: { label: 'Viewer', icon: Eye },
};

function RoleBadge({ role }) {
  const normalizedRole = (role || 'viewer').toLowerCase();
  const config = ROLE_CONFIG[normalizedRole] || {
    label: normalizedRole.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()),
    icon: User,
  };
  const Icon = config.icon;

  return (
    <Badge className={`inline-flex items-center gap-1 text-xs ${ROLE_BADGE_CLASS}`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

export default function DashboardLayout() {
  const { user, logout, hasPermission, hasRole, platformRole, tenant, isSuperadmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [openMenus, setOpenMenus] = useState({ 'Knowledge Base': true, People: false, Conversations: false, Engagement: false });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Apply brand colors from tenant
  useBrandColors();
  
  // Shepherd.js onboarding tour
  useOnboardingTour();

  // Get navigation config based on user permissions
  const navigationConfig = useMemo(() => 
    getNavigationConfig(hasPermission, hasRole),
    [hasPermission, hasRole]
  );

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleMenu = (label) => {
    setOpenMenus(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const closeSidebar = () => setSidebarOpen(false);
  const openEvaPeople = async () => {
    const fallbackUrl = process.env.REACT_APP_EVA_PEOPLE_URL || '/eva';
    const evaWindow = window.open('', '_blank');
    if (evaWindow) evaWindow.opener = null;
    try {
      const { url } = await authAPI.createEvaPeopleSSOLink();
      if (evaWindow) {
        evaWindow.location.href = url;
      } else {
        window.location.href = url;
      }
    } catch (_error) {
      if (evaWindow) {
        evaWindow.location.href = fallbackUrl;
      }
      toast.error('Unable to open Eva People with SSO', {
        description: 'Opening Eva People directly. You may need to sign in.',
      });
    }
  };

  // Dynamic tenant branding
  const tenantColor = tenant?.primary_color || '#6366f1';
  const tenantName = tenant?.display_name || 'Organization';

  return (
    <div className="flex h-dvh overflow-hidden bg-background" data-testid="dashboard-layout">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 lg:w-60 
        bg-card 
        border-r border-border 
        flex h-dvh max-h-dvh flex-col
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo with Tenant Branding - Fixed at top */}
        <div className="flex-shrink-0 p-4 border-b border-border flex items-center justify-between">
          <NavLink to="/overview" className="flex items-center gap-3" onClick={closeSidebar}>
            {/* Company icon from Brandfetch */}
            {(() => {
              const iconForTheme = theme === 'dark'
                ? (tenant?.icon_url_dark || tenant?.icon_url || tenant?.logo_url)
                : (tenant?.icon_url_light || tenant?.icon_url || tenant?.logo_url);
              
              if (iconForTheme) {
                const iconSrc = iconForTheme.startsWith('http') 
                  ? iconForTheme 
                  : `${process.env.REACT_APP_BACKEND_URL}${iconForTheme}`;
                
                return (
                  <img 
                    src={iconSrc}
                    alt={tenantName} 
                    className="w-9 h-9 rounded-lg object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                );
              }
              return null;
            })()}
            {/* Fallback initial */}
            <div 
              className="w-9 h-9 rounded-lg items-center justify-center text-white font-bold text-lg"
              style={{ 
                backgroundColor: tenantColor,
                display: (theme === 'dark'
                  ? (tenant?.icon_url_dark || tenant?.icon_url || tenant?.logo_url)
                  : (tenant?.icon_url_light || tenant?.icon_url || tenant?.logo_url)) ? 'none' : 'flex'
              }}
            >
              {tenantName.charAt(0)}
            </div>
            <div className="flex flex-col">
              <span className="text-base font-semibold text-foreground leading-tight">Eva Insights</span>
              <span className="text-xs text-muted-foreground">
                {(() => {
                  // Show domain without duplicating .com
                  const shortname = tenant?.shortname || '';
                  if (shortname.includes('.')) {
                    return shortname; // Already has domain extension
                  }
                  return shortname ? `${shortname}.com` : tenantName;
                })()}
              </span>
            </div>
          </NavLink>
          <Button 
            variant="ghost" 
            size="sm" 
            className="lg:hidden" 
            onClick={closeSidebar}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Navigation - Scrollable middle section */}
        <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto p-3">
          {navigationConfig.map((item) => (
            <NavGroup
              key={item.label}
              item={item}
              isOpen={openMenus[item.label]}
              onToggle={() => toggleMenu(item.label)}
              onNavigate={closeSidebar}
            />
          ))}
        </nav>

        {/* User Section - Fixed at bottom */}
        <div className="flex-shrink-0 border-t border-border p-3">
          <div className="flex items-center gap-3 px-3 py-2">
            <UserAvatar user={user} size="sm" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">
                {user?.first_name && user?.last_name 
                  ? `${user.first_name} ${user.last_name}`
                  : user?.name || user?.email?.split('@')[0]}
              </p>
              <RoleBadge role={platformRole} />
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {/* Trial Banner - shown for trial tenants */}
        <TrialBanner />

        {/* Paid-subscription grace/expired banner (EIP-322). Separate from TrialBanner,
            which only ever renders for subscription_status === 'trial'. */}
        <SubscriptionBanner />

        {/* Superadmin Banner */}
        <SuperadminBanner />
        
        {/* Header - Fixed */}
        <header className="flex-shrink-0 h-14 bg-card border-b border-border flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-toggle"
            >
              <Menu className="w-5 h-5" />
            </Button>
          </div>
          <div className="flex items-center gap-2">
            {/* Tenant Switcher for Superadmins */}
            <TenantSwitcher />
            <Button
              type="button"
              onClick={openEvaPeople}
              variant="outline"
              className="hidden rounded-full border-primary/25 bg-background/80 px-3 text-foreground shadow-sm backdrop-blur transition-all hover:border-primary/40 hover:bg-primary/5 hover:text-foreground sm:inline-flex"
              data-testid="open-eva-people-button"
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 ring-1 ring-primary/15">
                <img src={EVA_LOGO_URL} alt="" className="h-4.5 w-4.5 rounded object-cover" aria-hidden="true" />
              </span>
              <span className="font-medium">Eva People</span>
              <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
            </Button>
            <Button
              type="button"
              onClick={openEvaPeople}
              size="icon"
              variant="outline"
              className="rounded-full border-primary/25 bg-background/80 shadow-sm backdrop-blur hover:border-primary/40 hover:bg-primary/5 sm:hidden"
              data-testid="open-eva-people-button-mobile"
              aria-label="Open Eva People"
              title="Open Eva People"
            >
              <img src={EVA_LOGO_URL} alt="" className="h-5 w-5 rounded-md object-cover" aria-hidden="true" />
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              className="text-muted-foreground"
              data-testid="theme-toggle"
            >
              {theme === 'light' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="text-muted-foreground" data-testid="user-menu-trigger">
                  <Settings className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {/* Profile Settings */}
                <DropdownMenuItem onClick={() => navigate('/settings/profile')} data-testid="profile-settings-button">
                  <User className="w-4 h-4 mr-2" />
                  Profile Settings
                </DropdownMenuItem>
                
                <DropdownMenuSeparator />
                
                {/* Organization Section */}
                {hasRole(['admin', 'approver', 'analyst']) && (
                  <>
                    {hasRole(['admin', 'approver']) && (
                      <DropdownMenuItem onClick={() => navigate('/settings/organization')} data-testid="org-settings-button">
                        <Building2 className="w-4 h-4 mr-2" />
                        Organization Settings
                      </DropdownMenuItem>
                    )}
                    {hasRole(['admin', 'analyst']) && (
                      <DropdownMenuItem onClick={() => navigate('/settings/usage')} data-testid="token-usage-button">
                        <Zap className="w-4 h-4 mr-2" />
                        AI Usage
                      </DropdownMenuItem>
                    )}
                    {hasRole(['admin']) && (
                      <DropdownMenuItem onClick={() => navigate('/settings/billing')} data-testid="billing-settings-button">
                        <CreditCard className="w-4 h-4 mr-2" />
                        Billing & Subscription
                      </DropdownMenuItem>
                    )}
                    {hasRole(['admin', 'approver']) && (
                      <DropdownMenuItem onClick={() => navigate('/settings/audit-log')} data-testid="audit-log-button">
                        <Shield className="w-4 h-4 mr-2" />
                        Audit Log
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                  </>
                )}
                
                {/* Superadmin Section */}
                {isSuperadmin && (
                  <>
                    <DropdownMenuItem onClick={() => navigate('/superadmin/tenants')} data-testid="tenant-management-button">
                      <Building2 className="w-4 h-4 mr-2" />
                      Tenant Management
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/superadmin/pricing')} data-testid="pricing-config-button">
                      <DollarSign className="w-4 h-4 mr-2" />
                      Plans & Trials
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/superadmin/referrals')} data-testid="referral-management-button">
                      <Gift className="w-4 h-4 mr-2" />
                      Referral Programs
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/superadmin/integrations')} data-testid="integrations-button">
                      <Plug className="w-4 h-4 mr-2" />
                      Integrations
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/superadmin/theme')} data-testid="theme-customizer-button">
                      <Palette className="w-4 h-4 mr-2" />
                      Theme Customizer
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/superadmin/logs')} data-testid="application-logs-button">
                      <FileCode className="w-4 h-4 mr-2" />
                      Application Logs
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                
                {/* Sign Out */}
                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-button">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content - Scrollable */}
        <main className="min-h-0 flex-1 overflow-y-auto p-4 lg:p-6">
          <Outlet />
        </main>

        {/* Footer - Fixed */}
        <footer className="flex-shrink-0 h-12 bg-card border-t border-border flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-2">
            <img src={VISTRY_LOGO_URL} alt="Vistry" className="w-5 h-5 rounded" />
            <span className="text-sm font-semibold eva-gradient-text">vistry.ai</span>
          </div>
          <div className="flex items-center gap-4">
            <SupportTicketButton contextLabel={tenant?.tenant_id || tenant?.id || user?.tenant_id || ''} />
            <div className="text-xs text-muted-foreground hidden sm:block">
              {new Date().toLocaleString(undefined, { 
                timeZoneName: 'short',
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          </div>
        </footer>
      </div>
      
      {/* Welcome interstitial — enabled by default; respects tenant opt-outs and saved user completion. */}
      <OnboardingInterstitial />
    </div>
  );
}
